import { XSS_COLORS, type FieldDef, type ReportContext } from './context'
import { formatter } from './format'
import type { PowerMetric, PowerUnit, ReportData } from './types'

export type Stat = {
  key: string, label: string, short: string, value: string, unit: string, icon: string,
  diamonds?: number | null, // Rating: shown as diamonds with the number
  highest?: boolean, // the highest of the group
  // Remaining Recovery: one figure per system, each in its colour, shown in place of the value.
  parts?: { text: string, color: string, done: boolean }[],
}

/**
 * Days of recovery as the activity dashboard's "Recovery after" words it: under two days in
 * hours, then in days, and done once under an hour is left.
 */
function recoveryText(days: number): { text: string, done: boolean } {
  const hours = Math.round(days * 24)
  if (hours < 1) return { text: '', done: true }
  return { text: days < 2 ? `${hours}h` : `${Math.round(days)}d`, done: false }
}

// The power figures the power choice ("stat.powerChoice") can show, in the designer's order.
// Must match ReportTemplates::POWER_METRICS.
export const POWER_METRICS: PowerMetric[] = ['avg', 'mov', 'pedal', 'xep', 'rp']
export const POWER_LABELS: Record<PowerMetric, { label: string, short: string, choice: string }> = {
  avg: { label: 'Avg Power', short: 'Avg Power', choice: 'Average' },
  mov: { label: 'Moving Avg Power', short: 'Moving Avg', choice: 'Moving' },
  pedal: { label: 'Pedaling Avg Power', short: 'Pedaling Avg', choice: 'Pedaling' },
  xep: { label: 'Equivalent Power', short: 'XEP', choice: 'XEP' },
  rp: { label: 'Relative Power', short: 'Rel. Power', choice: 'Rel. Power' },
}

/** The power metrics this activity has, in the given unit. */
export function powerMetricsFor(data: ReportData, unit: PowerUnit): PowerMetric[] {
  return POWER_METRICS.filter((m) => data.activity.power?.[m]?.[unit] != null)
}

/**
 * The chosen power figure, or the first the activity has (Relative Power first) when it lacks it.
 * The chosen figure wins over the unit: Relative Power is in watts only, so it shows in watts
 * even when W/kg is picked.
 */
export function powerChoice(data: ReportData, options: { powerMetric: PowerMetric, powerUnit: PowerUnit }): { metric: PowerMetric, unit: PowerUnit } | null {
  const { powerMetric, powerUnit } = options
  const units: PowerUnit[] = [powerUnit, powerUnit === 'wkg' ? 'w' : 'wkg']
  const unit = units.find((u) => data.activity.power?.[powerMetric]?.[u] != null)
  if (unit) return { metric: powerMetric, unit }
  for (const unit of units) {
    const have = powerMetricsFor(data, unit)
    if (!have.length) continue
    return { metric: have.includes(powerMetric) ? powerMetric : (['rp', 'xep', 'avg', 'mov', 'pedal'] as PowerMetric[]).find((m) => have.includes(m))!, unit }
  }
  return null
}

type StatDef = {
  label: string,
  short?: string,
  icon: string,
  value: (d: ReportData['activity'], fmt: ReturnType<typeof formatter>) => string | null,
  unit: (fmt: ReturnType<typeof formatter>) => string,
}

// The activity statistics templates can show. Each is a toggleable field ("stat.<name>").
export const STATS: Record<string, StatDef> = {
  'stat.distance': { label: 'Distance', icon: 'route', value: (a, f) => f.distance(a.distance), unit: (f) => f.distanceUnit },
  'stat.time': { label: 'Moving Time', short: 'Time', icon: 'clock', value: (a, f) => f.duration(a.moving_time ?? a.duration), unit: () => '' },
  'stat.elevation': { label: 'Elevation', short: 'Climb', icon: 'mountain', value: (a, f) => (a.elevation_gain ? f.elevation(a.elevation_gain) : null), unit: (f) => f.elevationUnit },
  'stat.power': { label: 'Avg Power', short: 'Power', icon: 'bolt', value: (a, f) => f.int(a.avg_power), unit: () => 'W' },
  'stat.xep': { label: 'Equivalent Power', short: 'XEP Power', icon: 'bolt', value: (a, f) => f.int(a.xep), unit: () => 'W' },
  'stat.xss': { label: 'XSS', short: 'XSS', icon: 'flame', value: (a, f) => f.int(a.xss), unit: () => 'XSS' },
  'stat.difficulty': { label: 'Difficulty', icon: 'gauge', value: (a, f) => f.int(a.difficulty), unit: () => '' },
  'stat.rating': { label: 'Rating', icon: 'gauge', value: (a, f) => f.int(a.difficulty), unit: () => '' },
  // Label, value and unit come from the power choice (powerMetric / powerUnit options).
  'stat.powerChoice': { label: 'Power', icon: 'bolt', value: (a) => (a.power && POWER_METRICS.some((m) => a.power?.[m]) ? '' : null), unit: () => '' },
  'stat.hr': { label: 'Avg Heart Rate', short: 'Avg HR', icon: 'heart', value: (a, f) => f.int(a.avg_hr), unit: () => 'bpm' },
  'stat.speed': { label: 'Avg Speed', short: 'Speed', icon: 'speed', value: (a, f) => f.speed(a.avg_speed), unit: (f) => f.speedUnit },
  'stat.focus': { label: 'Focus', icon: 'target', value: (a, f) => f.focusDuration(a.focus_duration), unit: () => '' },
  'stat.carbs': { label: 'Carbs Burned', short: 'Carbs', icon: 'flame', value: (a, f) => f.int(a.carbs), unit: () => 'g' },
  'stat.calories': { label: 'Calories', icon: 'flame', value: (a, f) => f.int(a.calories), unit: () => 'kcal' },
  // Shown as low / high / peak figures (Stat.parts).
  'stat.recovery': { label: 'Remaining Recovery', short: 'Recovery', icon: 'pulse', value: (a) => (a.recovery && RECOVERY_SYSTEMS.some((s) => a.recovery?.[s.key] != null) ? '' : null), unit: () => '' },
}

const RECOVERY_SYSTEMS = [
  { key: 'low', color: XSS_COLORS.xlss },
  { key: 'high', color: XSS_COLORS.xhss },
  { key: 'peak', color: XSS_COLORS.xpss },
] as const

/** Field definitions for the given stats; `hidden` ones start switched off. */
export function statFields(keys: string[], hidden: string[] = []): FieldDef[] {
  return keys.map((key) => ({
    key,
    label: STATS[key].label,
    group: 'Content' as const,
    default: !hidden.includes(key),
    available: (d: ReportData) => STATS[key].value(d.activity, formatter(d.units)) !== null,
  }))
}

/**
 * The stats among `keys` that are switched on and have a value, in order. With `highest`, the
 * Rating, XSS and power stats are marked where the rider was the highest of their group.
 */
export function statsFor(ctx: ReportContext, keys: string[], { highest = false } = {}): Stat[] {
  const highs = highest ? ctx.data.group?.highest ?? [] : []
  const out: Stat[] = []
  for (const key of keys) {
    const def = STATS[key]
    if (!def || !ctx.show(key)) continue
    if (key === 'stat.powerChoice') {
      const choice = powerChoice(ctx.data, ctx.options)
      const figure = choice && ctx.data.activity.power?.[choice.metric]?.[choice.unit]
      if (!choice || figure == null) continue
      const label = POWER_LABELS[choice.metric]
      out.push({
        key, label: label.label, short: label.short, icon: def.icon,
        value: choice.unit === 'wkg' ? figure.toFixed(1) : ctx.fmt.int(figure)!,
        unit: choice.unit === 'wkg' ? 'W/kg' : 'W',
        highest: highs.includes(`power.${choice.metric}.${choice.unit}`),
      })
      continue
    }
    if (key === 'stat.recovery') {
      const recovery = ctx.data.activity.recovery
      const parts = RECOVERY_SYSTEMS.flatMap((s) => {
        const days = recovery?.[s.key]
        return days == null ? [] : [{ ...recoveryText(days), color: s.color }]
      })
      if (!parts.length) continue
      out.push({ key, label: def.label, short: def.short!, icon: def.icon, value: '', unit: '', parts })
      continue
    }
    const value = def.value(ctx.data.activity, ctx.fmt)
    if (value === null) continue
    const compared = key === 'stat.rating' ? 'difficulty' : key === 'stat.xss' ? 'xss' : null
    out.push({
      key, label: def.label, short: def.short ?? def.label, value, unit: def.unit(ctx.fmt), icon: def.icon,
      diamonds: key === 'stat.rating' ? ctx.data.activity.difficulty_stars : undefined,
      highest: !!compared && highs.includes(compared),
    })
  }
  return out
}
